import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPassword as apiResetPassword } from '../api/auth.ts';
import { PlatformApiError } from '../api/client.ts';
import { AuthPageFrame } from '../components/AuthPageFrame.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS_ON_LOWEST } from '../styles/forms.ts';

/**
 * Consumes a password-reset token from `?token=...`. Calls
 * `POST /v1/auth/password/reset` with the token + new password.
 *
 * Behavior:
 *   - Missing token → show an inline error; form disabled.
 *   - On success → confirmation pane + link to /login.
 *   - On error → surface platform-api envelope inline and via toast.
 *     All active sessions for the account are invalidated server-side
 *     (see platform-api reset handler); the UI doesn't need to
 *     auto-login.
 */

export function ResetPassword(): JSX.Element {
  const [search] = useSearchParams();
  const token = search.get('token');
  const { fetchImpl } = useSession();
  const toast = useToast();
  const t = useT();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const missingToken = token === null || token.length === 0;

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (missingToken) {
      setError(t('reset.error.missing_token'));
      return;
    }
    if (newPassword.length < 10) {
      setError(t('reset.error.password_too_short'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('reset.error.password_mismatch'));
      return;
    }
    setSubmitting(true);
    try {
      await apiResetPassword({ token: token ?? '', newPassword }, fetchImpl);
      setSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthPageFrame
      eyebrow={t('reset.eyebrow')}
      title={t('reset.subtitle')}
      description={t('reset.description')}
      maxWidthClassName="max-w-[460px]"
      footer={
        <div className="text-xs text-outline">
          <Link
            to="/login"
            data-testid="reset-back-login"
            className="rounded-lg hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
          >
            {t('common.back_to_login')}
          </Link>
        </div>
      }
    >

        {success ? (
          <div
            className="flex flex-col gap-3 rounded-lg bg-surface-container-low p-6"
            data-testid="reset-success"
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-primary">
              {t('reset.success.title')}
            </span>
            <p className="text-sm text-on-surface">{t('reset.success.body')}</p>
            <Link
              to="/login"
              data-testid="reset-login-link"
              className="mt-2 inline-flex w-fit items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)]"
            >
              {t('reset.success.sign_in')}
            </Link>
          </div>
        ) : (
          <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('reset.new_password')}
              </span>
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.currentTarget.value)}
                disabled={submitting || missingToken}
                required
                minLength={10}
                maxLength={128}
                data-testid="reset-new-password"
                className={INPUT_CLASS_ON_LOWEST}
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('reset.confirm_password')}
              </span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                disabled={submitting || missingToken}
                required
                minLength={10}
                maxLength={128}
                data-testid="reset-confirm-password"
                className={INPUT_CLASS_ON_LOWEST}
              />
            </label>
            {error !== null ? (
              <p
                className="m-0 text-xs font-medium text-error"
                role="alert"
                data-testid="reset-error"
              >
                {error}
              </p>
            ) : null}
            {missingToken ? (
              <p
                className="m-0 text-xs font-medium text-error"
                role="alert"
                data-testid="reset-missing-token"
              >
                {t('reset.error.token_banner')}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submitting || missingToken}
              data-testid="reset-submit"
              className="mt-2 inline-flex items-center justify-center rounded-xl bg-primary-container px-6 py-3 text-sm font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
            >
              {submitting ? t('reset.submitting') : t('reset.submit')}
            </button>
          </form>
        )}
    </AuthPageFrame>
  );
}
